import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import App from './App.jsx'
import ErrorBoundary from './components/layout/ErrorBoundary.jsx'
import './index.css'

const rootElement = document.getElementById('root')

// --- CHUNK LOAD ERROR RECOVERY ---
// Triggers a hard reload if a user navigates to a lazy-loaded route after a new deployment
// has replaced the old hashed JS chunks on the server.
// Reloads at most once per 30s so a persistently failing chunk cannot cause a reload loop.
const reloadOnce = () => {
  try {
    const last = Number(sessionStorage.getItem('chunk-reload-at') || 0);
    if (Date.now() - last < 30000) return;
    sessionStorage.setItem('chunk-reload-at', String(Date.now()));
  } catch { /* storage blocked: fall through and reload once per error */ }
  window.location.reload();
};

window.addEventListener('error', (e) => {
  if (e.message?.includes('Failed to fetch dynamically imported module') ||
      e.message?.includes('Importing a transcompiled module is not supported')) {
    reloadOnce();
  }
}, true);

window.addEventListener('unhandledrejection', (e) => {
  if (e.reason?.message?.includes('Failed to fetch dynamically imported module')) {
    reloadOnce();
  }
});

// Vite emits this when a preloaded chunk 404s after a new deploy.
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  reloadOnce();
});

// React Hydration / Mounting
// react-snap pre-renders each page for LCP + SEO.
// We use createRoot (not hydrateRoot) because framer-motion animations
// (navbar sliding from y:-100%, hero elements at opacity:0) create
// unavoidable hydration mismatches. React mounts cleanly over the
// pre-rendered snapshot — crawlers see full HTML, users get fast first paint.
//
// createRoot wipes the pre-rendered markup on first commit, and every page is a
// lazy chunk, so without a preload the visitor sees a blank black Suspense fallback
// until that chunk downloads (a long black screen on slow phones). Load the current
// route's chunk first so the pre-rendered page stays on screen until React can
// replace it with real content. Capped at 4s so a failing chunk never blocks mounting.
const ROUTE_CHUNKS = [
  [/^\/$/, () => import('./pages/Home.jsx')],
  [/^\/services$/, () => import('./pages/Services.jsx')],
  [/^\/services\/[^/]+$/, () => import('./pages/ServiceDetail.jsx')],
  [/^\/portfolio$/, () => import('./pages/Portfolio.jsx')],
  [/^\/portfolio\/[^/]+$/, () => import('./pages/CaseStudy.jsx')],
  [/^\/about$/, () => import('./pages/About.jsx')],
  [/^\/blog$/, () => import('./pages/Blog.jsx')],
  [/^\/blog\/[^/]+$/, () => import('./pages/BlogPost.jsx')],
  [/^\/contact$/, () => import('./pages/Contact.jsx')],
  [/^\/privacy-policy$/, () => import('./pages/PrivacyPolicy.jsx')],
  [/^\/terms-of-service$/, () => import('./pages/TermsOfService.jsx')],
]

const mountApp = () => {
  createRoot(rootElement).render(
    <StrictMode>
      <HelmetProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </BrowserRouter>
      </HelmetProvider>
    </StrictMode>
  )

  // Signal to the index.html shell that we are ready to unveil
  if (typeof window.markAppReady === 'function') {
    window.markAppReady();
  }
}

const currentPath = window.location.pathname.replace(/\/+$/, '') || '/'
const loadRoute = ROUTE_CHUNKS.find(([re]) => re.test(currentPath))?.[1]

Promise.race([
  loadRoute ? loadRoute().catch(() => {}) : Promise.resolve(),
  new Promise((resolve) => setTimeout(resolve, 4000)),
]).then(mountApp)



